import { keepPreviousData, useQuery } from "@tanstack/react-query";
import { getJson } from "./getJson.js";

/**
 * Typeahead matches for the symbol box and the command palette.
 *
 * Its own file rather than another entry in `queries.ts`, which holds the
 * page-level reads: this one fires on a keystroke and has a debounce, a
 * minimum length, and a placeholder policy none of those need.
 */

export interface InstrumentMatch {
  symbol: string;
  name: string;
  sector: string | null;
}

/**
 * Matches the server's `MIN_QUERY`. Checked here as well so one character
 * never leaves the browser: the request would come back empty and the round
 * trip is the whole cost.
 */
export const MIN_QUERY = 2;

export function useInstrumentSearch(query: string) {
  const trimmed = query.trim();
  const enabled = trimmed.length >= MIN_QUERY;

  return useQuery({
    queryKey: ["instruments", "search", trimmed],
    queryFn: () =>
      getJson<{ matches: InstrumentMatch[] }>(
        `/api/instruments/search?q=${encodeURIComponent(trimmed)}`,
      ),
    enabled,
    // The list a reader is reading stays on screen while the next keystroke
    // resolves. Without it the dropdown empties and re-fills on every letter,
    // which reads as flicker rather than as narrowing.
    placeholderData: keepPreviousData,
    // A ticker list changes once a day, when `kite-instruments` runs at 08:00.
    staleTime: 5 * 60_000,
  });
}
